import { useState } from 'react';
import { toast } from 'sonner';
import { getAuthToken } from '@/utils/auth';

interface UploadResponse {
  success: boolean;
  data?: any;
  error?: string;
}

interface CloudinaryResponse {
  public_id: string;
  secure_url: string;
  url: string;
  error?: any;
  message?: string;
}

export const useUploadProfessionalDocuments = (doctorId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadToCloudinary = async (file: File): Promise<string | null> => {
    try {
      const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
      const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
      const folder = import.meta.env.VITE_CLOUDINARY_FOLDER;

      if (!cloudName || !uploadPreset) {
        throw new Error('Cloudinary configuration missing. Check .env file.');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', folder);
      formData.append('resource_type', 'auto');

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data: CloudinaryResponse = await cloudinaryResponse.json();

      if (!cloudinaryResponse.ok) {
        throw new Error(
          data.message || data.error?.message || 'Erro ao fazer upload para Cloudinary'
        );
      }

      return data.secure_url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro unknown';
      console.error('Cloudinary upload error:', err);
      throw new Error(errorMessage);
    }
  };

  const uploadDocuments = async (file: File): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Upload file to Cloudinary
      const documentUrl = await uploadToCloudinary(file);
      
      if (!documentUrl) {
        throw new Error('Falha ao obter URL do documento');
      }

      // 2. Save document URL to backend
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Token de autenticação não encontrado. Faça login novamente.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/doctors/${doctorId}/professional-documents`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            documentUrl: documentUrl,
          }),
        }
      );

      const data: UploadResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao salvar documentos no servidor');
      }

      toast.success('✓ Documentos profissionais enviados com sucesso!');
      setIsLoading(false);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
      return false;
    }
  };

  return { uploadDocuments, isLoading, error };
};
